import { useRef, useState, useEffect } from 'react';
import { Transition } from 'react-transition-group';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { PrimaryButton, PrimaryButtonOldUi,/*, NavButtonGroup*/ } from '../components/buttons';
import { VTControlBar } from '../components/VTControls';

import {
  pageTransitionDurationMs,
  transitionStylesCrossFadeIn,
} from '../components/transitions';
import { BodyExtraLarge, BodyMedium, BodySmall } from '../components/typography';
import ZoneSelector from '../components/ZoneSelector';

import Header from '../components/Header';

const USE_MODERN_PLAYER = false;

export default function HomeVirtualTour ({ dispatch, state }) {
  const { currentMode, currentScreen, selectedHome, isPreviewing, vtCurrentRoomName, vtCurrentZoneName, vtSelectedHotspot, SCREENS, MODES } = state;
  const showControls = (
    currentScreen === SCREENS.HOME_VIRTUAl_TOUR &&
    currentMode === MODES.WEB &&
    !vtSelectedHotspot
  );
  
  const [delayedShowControls, setDelayedShowControls] = useState(showControls);
  const [isMinimized, setIsMinimized] = useState(true);  
  const [arePreviewsVisible, setArePreviewsVisible] = useState(false);

  useEffect(() => {
    setDelayedShowControls(showControls);
  }, [showControls]);

  const animationStyle = {
    opacity: 0,
    transition: 'opacity 350ms ease-in-out',
    '&.delayed-open': {
      opacity: 1,
    },
  };

  const currentRoom = vtCurrentRoomName || vtCurrentZoneName || '';
  const nodeRef = useRef(null);
 
  const modernUi = () => {
    return (
      <Transition
      in={currentScreen === SCREENS.HOME_VIRTUAl_TOUR}
      nodeRef={nodeRef}
      timeout={pageTransitionDurationMs}
      mountOnEnter={false}  // !!! MUST BE FALSE FOR VT PLAYER TO WORK !!!
      unmountOnExit={false} // !!! MUST BE FALSE FOR VT PLAYER TO WORK !!!
    >
      {transitionState => {
        return (          
          <Box
            ref={nodeRef}
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              zIndex: 1,
              height: '100%',
              width: '100%',
              transition: `all ${pageTransitionDurationMs}ms ease-in-out`,
              ...transitionStylesCrossFadeIn['exited'],
              ...transitionStylesCrossFadeIn[transitionState],
            }}
          >
            {(currentMode === MODES.WEB && !isPreviewing) && (
              <Header dispatch={dispatch} state={state} />
            )}

            <Stack
              className={delayedShowControls ? 'delayed-open' : ''}
              direction='column'
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                pb: 4.5,
                pl: 4,
                ...animationStyle,
              }}
            >
              <BodySmall
                sx={{
                  fontWeight: 500,
                  mb: 1.5,
                  textTransform: 'uppercase',
                }}
              >
                {currentRoom}
              </BodySmall>
              <BodySmall
                sx={{
                  fontWeight: 500,
                  maxWidth: '12.5rem',
                }}
              >
                Room Description Goes Here
              </BodySmall>
              <Box
                sx={{
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  height: 0,
                  my: 2,
                  width: '5.625rem',
                }}
              />
              <BodyExtraLarge>
                {(selectedHome?.availability === 'SOLD') ? 'SOLD' : selectedHome?.price}
              </BodyExtraLarge>
              {selectedHome?.payment && (
                <BodyMedium
                  sx={{
                    fontFamily: 'Reckless',
                    mb: 3
                  }}
                >
                  {selectedHome?.payment}
                </BodyMedium>
              )}
              <PrimaryButton
                label='Reserve Now'
                onClick={() => dispatch({ type: 'reserve-now-button-clicked' })}
                size='large'
              />
            </Stack>
            
            <Stack
              className={delayedShowControls ? 'delayed-open' : ''}
              direction='row'
              spacing={1.5}
              sx={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                pb: 4.5,
                pr: 2,
                ...animationStyle,
              }}
            >
              <ZoneSelector
                dispatch={dispatch}
                state={state}
              />
              {/* HIDING THIS UI UNTIL WE SUPPORT EXTERIOR AND VIDEO TOURS IN NORMA */}
              {/* <NavButtonGroup
                dispatch={dispatch}
                state={state}
              /> */}
            </Stack>
  
            <canvas id='renderCanvas' key='renderCanvas' style={{ zIndex: -1 }}></canvas>
            <canvas id='topdownMapCanvas' key='mapCanvasCanvas' style={{ zIndex: -1 }}></canvas>
          </Box>
        );
      }}
    </Transition>
    );
  }

  const oldUiMock = () => {
    const cameras = state.selectedHome?.tour.json.cameras;

    return (
      <Transition
        in={currentScreen === SCREENS.HOME_VIRTUAl_TOUR}
        nodeRef={nodeRef}
        timeout={pageTransitionDurationMs}
        mountOnEnter={false}  // !!! MUST BE FALSE FOR VT PLAYER TO WORK !!!
        unmountOnExit={false} // !!! MUST BE FALSE FOR VT PLAYER TO WORK !!!
      >
        {transitionState => {
          return (          
            <Box
              ref={nodeRef}
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                zIndex: 1,
                height: '100%',
                width: '100%',
                transition: `all ${pageTransitionDurationMs}ms ease-in-out`,
                ...transitionStylesCrossFadeIn['exited'],
                ...transitionStylesCrossFadeIn[transitionState],
              }}
            >
              <VTControlBar
                delayedShowControls={delayedShowControls}
                cameras={cameras ?? []}
                isMinimized={isMinimized}
                arePreviewsVisible={arePreviewsVisible}
                setArePreviewsVisible={setArePreviewsVisible}
                animationStyle={animationStyle}
                setIsMinimized={setIsMinimized}
              />
              <Stack
                className={delayedShowControls ? 'delayed-open' : ''}
                direction='row'
                spacing={1.5}
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  pb: 4.5,
                  pr: 2,
                  ...animationStyle,
                }}
              >
              </Stack>
              <canvas id='renderCanvas' key='renderCanvas' style={{ zIndex: -1 }}></canvas>
              <canvas id='topdownMapCanvas' key='mapCanvasCanvas' style={{ zIndex: 0 }}></canvas>
            </Box>
          );
        }}
      </Transition>
    );
  }

  if(USE_MODERN_PLAYER) {
    return modernUi();
  } 
  else {
    return oldUiMock();
  }

}
